import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { debounce } from 'lodash';
import { toast } from 'react-hot-toast';

export default function NotesModal({ isOpen, onClose, skill, subtopic }) {
  const [content, setContent] = useState('');
  const [isPreview, setIsPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Initialize content when modal opens
  useEffect(() => {
    if (isOpen && subtopic) {
      setContent(subtopic.notes || '');
      setHasUnsavedChanges(false);
    }
  }, [isOpen, subtopic]);

  // Handle save with proper error handling
  const saveNotes = useCallback(async (newContent) => {
    if (!skill?.id || !subtopic?.id) {
      toast.error('Cannot save notes: Invalid skill or subtopic');
      return;
    }

    try {
      setIsSaving(true);
      const skillRef = doc(db, 'skills', skill.id);
      
      // First, get the current skill data to ensure we have the latest state
      const skillDoc = await getDoc(skillRef);
      if (!skillDoc.exists()) {
        toast.error('Skill not found');
        return;
      }

      const currentSkillData = skillDoc.data();
      const subtopicIndex = currentSkillData.subtopics.findIndex(s => s.id === subtopic.id);
      
      if (subtopicIndex === -1) {
        toast.error('Cannot save notes: Subtopic not found');
        return;
      }

      // Create a new subtopics array with the updated notes
      const updatedSubtopics = [...currentSkillData.subtopics];
      updatedSubtopics[subtopicIndex] = {
        ...updatedSubtopics[subtopicIndex],
        notes: newContent,
        lastUpdated: new Date().toISOString()
      };

      // Update the entire skill document with the new subtopics array
      await updateDoc(skillRef, {
        subtopics: updatedSubtopics
      });

      setHasUnsavedChanges(false);
      toast.success('Notes saved successfully');
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setIsSaving(false);
    }
  }, [skill?.id, subtopic?.id]);

  // Debounced save
  const debouncedSave = useCallback(
    debounce((newContent) => {
      saveNotes(newContent);
    }, 1000),
    [saveNotes]
  );

  // Handle content changes
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    setHasUnsavedChanges(true);
    debouncedSave(newContent);
  };

  // Handle manual save
  const handleManualSave = async (e) => {
    if (e) {
      e.preventDefault();
    }
    await saveNotes(content);
  };

  // Handle modal close with unsaved changes warning
  const handleClose = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    // Tab key handling
    if (e.key === 'Tab') {
      e.preventDefault();
      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;
      const newContent = content.substring(0, start) + '  ' + content.substring(end);
      setContent(newContent);
      setTimeout(() => {
        e.target.selectionStart = e.target.selectionEnd = start + 2;
      }, 0);
    }

    // Save on Ctrl/Cmd + S
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      handleManualSave();
    }
  };

  if (!isOpen || !subtopic) return null;

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose}></div>
      
      <div className="flex min-h-full items-center justify-center p-4 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-3xl transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 text-left shadow-xl transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Notes for {subtopic.name}
                </h3>
                {hasUnsavedChanges && (
                  <span className="text-sm text-yellow-600 dark:text-yellow-400 ml-2">
                    Unsaved changes
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                 {isSaving && (
                  <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">
                    Saving...
                  </span>
                )}
                <button
                  onClick={() => setIsPreview(!isPreview)}
                  className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {isPreview ? 'Edit' : 'Preview'}
                </button>
                <button
                  onClick={handleManualSave}
                  className="px-3 py-1 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={handleClose}
                  className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>

          <div className="px-6 py-4">
            <div className="space-y-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
                {isPreview ? (
                  <div className="p-4 prose dark:prose-invert max-w-none">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {content}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <textarea
                    value={content}
                    onChange={handleContentChange}
                    onKeyDown={handleKeyDown}
                    className="w-full h-96 p-4 bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-0 focus:ring-2 focus:ring-primary-500 resize-none font-mono"
                    placeholder="Write your notes here... (Markdown supported)"
                    autoFocus
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm text-gray-500 dark:text-gray-400">
                <div>
                  <h4 className="font-medium mb-2">Markdown Formatting</h4>
                  <div className="space-y-1 font-mono">
                    <p># Heading 1</p>
                    <p>## Heading 2</p>
                    <p>### Heading 3</p>
                    <p>*italic* or _italic_</p>
                    <p>**bold** or __bold__</p>
                    <p>- Bullet point</p>
                    <p>1. Numbered list</p>
                    <p>[Link text](url)</p>
                    <p>![Image alt](url)</p>
                    <p>`inline code`</p>
                    <p>```code block```</p>
                    <p>&gt; Blockquote</p>
                    <p>--- (horizontal rule)</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Keyboard Shortcuts</h4>
                  <div className="space-y-1">
                    <p>Ctrl/Cmd + S: Save</p>
                    <p>Tab: Indent</p>
                    <p>Shift + Tab: Unindent</p>
                    <p>Ctrl/Cmd + B: Bold</p>
                    <p>Ctrl/Cmd + I: Italic</p>
                    <p>Ctrl/Cmd + K: Link</p>
                    <p>Ctrl/Cmd + Shift + C: Code block</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
} 